// Auftrag → Panel mit Checkliste + Anhänge + zugeordneten Aufgaben.
// Ersetzt seit Etappe A (27.02.2026) die globale Anzeige von Order-Tasks im
// Kanban. Wird aus `OrdersPage.js` beim Klick auf „Aufgaben & Checkliste"
// pro Auftrag geöffnet.
import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { X, Loader2, Upload, Trash2, Download, FileText, CheckCircle2, Lock, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import TechnikChecklistModal from '../../components/TechnikChecklistModal';

const KIND_LABELS = {
  shipping_label: 'Speditionslabel',
  delivery_note:  'Lieferschein',
  other:          'Anhang',
};

const INTERNAL_NOTE_ROLES = new Set(['admin', 'innendienst', 'team']);

const ATTACHMENT_ITEM_KEY = 'shipping_ready';   // muss zum Backend passen

export default function OrderTasksChecklistPanel({ order, onClose, onChanged, inline = false, hideInternalNote = false, hideAttachedTasks = false }) {
  const { user } = useAuth();
  const canSeeInternalNote = INTERNAL_NOTE_ROLES.has((user?.role || '').toLowerCase());
  const [checklist, setChecklist] = useState([]);
  const [attachments, setAttachments] = useState([]);
  // 01.09.2026 (Bug-Fix): Paletten hier lokal cachen, damit der
  // PaletteEditor beim Reload/nach dem Speichern die Werte behält —
  // der `order`-Prop wird vom Parent nach dem Speichern nicht neu geladen.
  const [pallets, setPallets] = useState(order.pallets || []);
  const [tasks, setTasks] = useState([]);
  const [internalNote, setInternalNote] = useState('');
  const [internalNoteMeta, setInternalNoteMeta] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);
  // 01.09.2026: Modal für die Technik-Systemcheckliste (Arbeitsanweisung
  // Software) — wird unter `commissioning_done` geöffnet.
  const [technikModalOpen, setTechnikModalOpen] = useState(false);
  // Systemcheckliste ist nur für HANS LS/HS/GS 6..90 relevant, nicht für
  // Zubehör / GS HV / GS-C HVP. Backend liefert `required`-Flag.
  const [technikRequired, setTechnikRequired] = useState(false);
  useEffect(() => {
    if (order.orderKind === 'bestellung') { setTechnikRequired(false); return; }
    authFetch(`/api/integrations/easybill/orders/${order.id}/technik-checklist`)
      .then(r => r.ok && r.json())
      .then(d => setTechnikRequired(!!d?.required))
      .catch(() => {});
  }, [order.id, order.orderKind]);

  const load = useCallback(async () => {
    // 01.09.2026 (Bug-Fix): Skeleton nur beim ersten Laden zeigen.
    // Sonst unmountet React die Checkliste (inkl. offener
    // TechnikChecklistModal) bei jedem Speichern.
    setChecklist(prev => {
      if (!prev || prev.length === 0) setLoading(true);
      return prev;
    });
    setLoadError(null);
    const promises = [
      authFetch(`/api/integrations/easybill/orders/${order.id}/checklist`),
      authFetch(`/api/integrations/easybill/orders/${order.id}/tasks`),
    ];
    if (canSeeInternalNote) {
      promises.push(authFetch(`/api/integrations/easybill/orders/${order.id}/internal-note`));
    }
    const [cRes, tRes, nRes] = await Promise.all(promises);
    if (cRes.ok) {
      const d = await cRes.json();
      setChecklist(d.checklist || []);
      setAttachments(d.attachments || []);
      if (Array.isArray(d.pallets)) setPallets(d.pallets);
    } else if (cRes.status === 403) {
      const d = await cRes.json().catch(() => ({}));
      setLoadError(d.detail || 'Ihrer Rolle fehlt die Berechtigung zum Anzeigen der Checkliste (orders.read).');
    } else {
      setLoadError('Checkliste konnte nicht geladen werden.');
    }
    if (tRes.ok) {
      const d = await tRes.json();
      setTasks(d.tasks || []);
    }
    if (nRes && nRes.ok) {
      const d = await nRes.json();
      setInternalNote(d.note || '');
      setInternalNoteMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy });
    }
    // Auftrag als gelesen markieren (Bell verschwindet)
    authFetch(`/api/integrations/easybill/orders/${order.id}/activity/read`, { method: 'POST' });
    setLoading(false);
  }, [order.id, canSeeInternalNote]);

  useEffect(() => { load(); }, [load]);

  const toggleItem = async (item) => {
    // optimistisch
    const wasDone = !!item.done;
    setChecklist(cl => cl.map(x => x.id === item.id ? { ...x, done: !wasDone } : x));
    const r = await authFetch(
      `/api/integrations/easybill/orders/${order.id}/checklist/${item.id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done: !wasDone }) },
    );
    if (r.ok) {
      const d = await r.json();
      setChecklist(d.checklist || []);
      if (d.autoCompleted) {
        toast.success('Auftrag automatisch als erledigt markiert — alle Punkte abgehakt.');
      } else if (d.sectionMovedTo) {
        const LABEL = { new: 'Neu', in_progress: 'In Bearbeitung',
                        production: 'Produktion', done: 'Erledigt' };
        toast.success(`Prozessabschnitt abgeschlossen — Auftrag wechselt in „${LABEL[d.sectionMovedTo] || d.sectionMovedTo}".`);
      }
      onChanged?.();
    } else {
      // rollback
      setChecklist(cl => cl.map(x => x.id === item.id ? { ...x, done: wasDone } : x));
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Konnte Checklisten-Item nicht ändern');
    }
  };

  const uploadFiles = async (fileList, kind = 'other') => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      const r = await authFetch(
        `/api/integrations/easybill/orders/${order.id}/attachments?kind=${encodeURIComponent(kind)}`,
        { method: 'POST', body: fd },
      );
      if (r.ok) {
        const d = await r.json();
        setAttachments(a => [...a, d]);
        toast.success(`${f.name} hochgeladen`);
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(`${f.name}: ${d.detail || 'Upload fehlgeschlagen'}`);
      }
    }
    setUploading(false);
    onChanged?.();
  };

  const download = async (att) => {
    const r = await authFetch(
      `/api/integrations/easybill/orders/${order.id}/attachments/${att.id}`,
    );
    if (!r.ok) { toast.error('Download fehlgeschlagen'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = att.fileName || 'anhang';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Anhang „${att.fileName}" wirklich entfernen?`)) return;
    const r = await authFetch(
      `/api/integrations/easybill/orders/${order.id}/attachments/${att.id}`,
      { method: 'DELETE' },
    );
    if (r.ok) {
      setAttachments(a => a.filter(x => x.id !== att.id));
      onChanged?.();
    } else toast.error('Anhang konnte nicht entfernt werden');
  };

  const saveInternalNote = async () => {
    setNoteSaving(true);
    const r = await authFetch(
      `/api/integrations/easybill/orders/${order.id}/internal-note`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: internalNote }) },
    );
    setNoteSaving(false);
    if (r.ok) {
      const d = await r.json();
      setInternalNoteMeta({ updatedAt: d.updatedAt, updatedBy: d.updatedBy });
      toast.success('Interne Notiz gespeichert');
      onChanged?.();
    } else toast.error('Notiz konnte nicht gespeichert werden');
  };

  // ── Drag & Drop-Handler für Anhänge (nur wenn Item „Versandfertig") ──
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) uploadFiles(files, 'other');
  };

  return (
    <PanelShell inline={inline} order={order} onClose={onClose}>
        <div className={`${inline ? '' : 'p-5'} overflow-y-auto space-y-6`}>
          {loading && (
            <div className="text-sm text-gray-500 flex items-center gap-2 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Lade …
            </div>
          )}

          {!loading && loadError && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-3"
                 data-testid="order-checklist-error">
              {loadError}
            </div>
          )}

          {!loading && !loadError && (
            <>
              {/* ─── Checkliste (Prozessabschnitte + Rollen-Badges,
                  28.02.2026 User-Wunsch) ─── */}
              <section>
                <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">Checkliste</h3>
                <div className="space-y-4">
                  {(() => {
                    // Rollen → Farbe.
                    // Innendienst / Technik: aus User-Profil calendarColor.
                    // Lager / Produktion: hart im Frontend gesetzt, weil unter
                    // Einstellungen zwar nur eine kombinierte Nutzerrolle
                    // existiert, im Auftrags-Workflow aber visuell klar
                    // zwischen Lager (Orange) und Produktion (Braun)
                    // unterschieden werden muss. (User-Wunsch 28.02.2026)
                    const ROLE_COLOR = {
                      innendienst: '#8080c0',
                      lager:       '#ff8000',  // Orange
                      produktion:  '#8B4513',  // Braun (SaddleBrown)
                      technik:     '#0080ff',  // Thomas Fasel calendarColor
                    };
                    const ROLE_LABEL = {
                      innendienst: 'Innendienst',
                      lager:       'Lager',
                      produktion:  'Produktion',
                      technik:     'Technik',
                    };
                    // Legacy-Items ohne section einfach unter „Sonstiges"
                    const grouped = [];
                    const seen = new Map();
                    for (const it of checklist) {
                      const sec = it.section || 'Sonstiges';
                      if (!seen.has(sec)) {
                        seen.set(sec, grouped.length);
                        grouped.push({ section: sec, items: [] });
                      }
                      grouped[seen.get(sec)].items.push(it);
                    }
                    return grouped.map(({ section, items }) => (
                      <div key={section}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#7ca735] mb-1.5">
                          {section}
                        </div>
                        <div className="space-y-1.5">
                          {items.map(it => (
                            <div key={it.id}
                                 className={`flex flex-col gap-1 px-3 py-2 rounded ${it.done ? 'bg-emerald-50' : 'bg-gray-50'}`}
                                 data-testid={`checklist-item-${it.key}`}>
                              <div className="flex items-start gap-2">
                                <Checkbox
                                  checked={!!it.done}
                                  onCheckedChange={() => toggleItem(it)}
                                  className="mt-0.5"
                                  data-testid={`checklist-toggle-${it.key}`}
                                />
                                <div className="flex-1 text-sm min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={it.done ? 'text-emerald-700 line-through' : 'text-[#32373c]'}>
                                      {it.label}
                                    </span>
                                    {it.role && (
                                      <span
                                        className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-medium tracking-wide"
                                        style={{ backgroundColor: ROLE_COLOR[it.role] || '#6b7280' }}
                                        data-testid={`role-badge-${it.key}`}
                                      >
                                        {ROLE_LABEL[it.role] || it.role}
                                      </span>
                                    )}
                                  </div>
                                  {it.done && it.doneBy && (
                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                      ✓ {it.doneBy} · {it.doneAt ? new Date(it.doneAt).toLocaleString('de-DE') : ''}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Palette-Editor unter „Versandbereitschaft
                                  herstellen" — nur bei Aufträgen, nicht
                                  bei Bestellungen (User-Wunsch 01.09.2026). */}
                              {it.key === 'ready_to_ship' && order.orderKind !== 'bestellung' && (
                                <PaletteEditor
                                  orderId={order.id}
                                  initial={pallets}
                                  onSaved={(saved) => {
                                    if (Array.isArray(saved)) setPallets(saved);
                                    onChanged?.();
                                  }}
                                />
                              )}
                              {/* 01.09.2026: Button „Systemcheckliste"
                                  unter dem Punkt „System konfigurieren
                                  und in Betrieb nehmen". Der Techniker
                                  öffnet die Arbeitsanweisung, hakt alle
                                  34 Punkte ab, trägt die VRM ID ein und
                                  lädt am Ende das PDF herunter. Erst
                                  dann darf `commissioning_done` in der
                                  Auftrags-Checkliste abgehakt werden
                                  (Backend-Guard). */}
                              {it.key === 'commissioning_done' && order.orderKind !== 'bestellung' && technikRequired && (
                                <div className="mt-2 ml-6">
                                  <button
                                    type="button"
                                    onClick={() => setTechnikModalOpen(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#0080ff]/40 bg-[#0080ff]/5 text-[#0080ff] text-xs font-medium hover:bg-[#0080ff]/10"
                                    data-testid={`btn-technik-checklist-${it.key}`}
                                  >
                                    <ClipboardCheck className="w-3.5 h-3.5" />
                                    Systemcheckliste öffnen
                                  </button>
                                  <span className="text-[10px] text-gray-500 ml-2">
                                    Muss vollständig abgehakt + VRM ID geprüft sein.
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </section>

              {/* ─── Internes Notizfeld (nur Innendienst/Team/Admin) ─── */}
              {canSeeInternalNote && !hideInternalNote && (
                <section data-testid="order-internal-note-section">
                  <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2 flex items-center gap-1.5">
                    <Lock className="w-3 h-3" /> Interne Notiz (Innendienst/Team)
                  </h3>
                  <Textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Wichtige Hinweise, Absprachen mit Kunden, nachträgliche Infos aus Telefonaten …"
                    rows={4}
                    className="text-xs"
                    data-testid="order-internal-note-textarea"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-[10px] text-gray-400">
                      {internalNoteMeta?.updatedAt && (
                        <>Zuletzt: {new Date(internalNoteMeta.updatedAt).toLocaleString('de-DE')}
                          {internalNoteMeta.updatedBy && ` · ${internalNoteMeta.updatedBy}`}</>
                      )}
                    </div>
                    <Button
                      onClick={saveInternalNote}
                      disabled={noteSaving}
                      className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs h-7"
                      data-testid="btn-save-internal-note"
                    >
                      {noteSaving ? 'Speichere …' : 'Notiz speichern'}
                    </Button>
                  </div>
                </section>
              )}

              {/* ─── Anhänge (Versandfertig) ─── */}
              <section>
                <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
                  Anhänge (Versandfertig) — Speditionslabel, Lieferschein
                </h3>
                <div
                  onDrop={onDrop}
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onClick={() => fileRef.current?.click()}
                  className={`rounded border-2 border-dashed p-4 text-center text-xs cursor-pointer transition-colors ${
                    dragActive ? 'border-[#9ec043] bg-[#f8faf2]' : 'border-gray-200 bg-gray-50 hover:border-[#9ec043]'
                  }`}
                  data-testid="order-attachment-dropzone"
                >
                  <Upload className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                  <div className="text-[#32373c] font-medium">Dateien hier ablegen oder auswählen</div>
                  <div className="text-[10px] text-gray-400">z. B. PDF, JPG, PNG — max. 8 MB je Datei</div>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    onChange={(e) => { uploadFiles(e.target.files, 'other'); if (e.target) e.target.value = ''; }}
                    className="hidden"
                    data-testid="order-attachment-input"
                  />
                </div>
                {uploading && (
                  <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Wird hochgeladen …
                  </div>
                )}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="order-attachment-list">
                    {attachments.map(a => (
                      <div key={a.id}
                           className="flex items-center justify-between bg-white border border-gray-100 rounded px-3 py-2 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-[#9ec043] shrink-0" />
                          <span className="truncate">{a.fileName}</span>
                          <span className="text-gray-400 shrink-0 text-[10px]">
                            · {KIND_LABELS[a.kind] || a.kind} · {(a.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => download(a)}
                                  className="text-gray-400 hover:text-[#9ec043]"
                                  title="Herunterladen"
                                  data-testid={`attachment-download-${a.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => removeAttachment(a)}
                                  className="text-gray-400 hover:text-rose-600"
                                  title="Entfernen"
                                  data-testid={`attachment-remove-${a.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ─── Zugeordnete Aufgaben ─── */}
              {!hideAttachedTasks && (
              <section>
                <h3 className="text-xs font-semibold uppercase text-gray-400 mb-2">
                  Aufgaben zu diesem Auftrag ({tasks.length})
                </h3>
                {tasks.length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">
                    Keine Aufgaben — die Checkliste oben ersetzt den bisherigen Workflow.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {tasks.map(t => (
                      <div key={t.id}
                           className="border border-gray-100 rounded px-3 py-2 text-xs bg-white flex items-start justify-between gap-3"
                           data-testid={`order-task-${t.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#32373c]">{t.title}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{t.status}</span>
                            {t.assignees?.length > 0 && <span>· {t.assignees.join(', ')}</span>}
                            {t.dueDate && <span>· fällig {new Date(t.dueDate).toLocaleDateString('de-DE')}</span>}
                            {t.createdAt && <span>· angelegt {new Date(t.createdAt).toLocaleString('de-DE')}</span>}
                          </div>
                        </div>
                        {t.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />}
                      </div>
                    ))}
                  </div>
                )}
              </section>
              )}
            </>
          )}
        </div>
        {technikModalOpen && (
          <TechnikChecklistModal
            orderId={order.id}
            onClose={() => setTechnikModalOpen(false)}
            onCompleted={() => { onChanged?.(); }}
          />
        )}
    </PanelShell>
  );
}

// Wrapper: entweder Modal-Chrome (Default) oder Inline (in anderen Modals).
function PanelShell({ inline, order, onClose, children }) {
  if (inline) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="order-checklist-inline">
        {children}
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="order-checklist-panel">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-[#32373c]">
              Aufgaben & Checkliste
            </h2>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {order.easybillDocumentNumber || 'Entwurf'} · {order.customerCompany || order.customerName || '—'}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="btn-checklist-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <Button onClick={onClose} className="text-sm">Schließen</Button>
        </div>
      </div>
    </div>
  );
}


// 28.02.2026: Paletten-Editor unter „Versandbereit". Erfasst pro Palette
// Gefahrgut-Flag + Maße (LxBxH in Metern) + Gewicht (kg). Speichert
// `orders.pallets = [{id, hazardous, lengthM, widthM, heightM, weightKg}]`.
function PaletteEditor({ orderId, initial, onSaved }) {
  const [pallets, setPallets] = useState(() =>
    (initial && initial.length > 0)
      ? initial.map((p, i) => ({ ...p, _key: i }))
      : [{ _key: 0, hazardous: false, lengthM: '', widthM: '', heightM: '', weightKg: '' }],
  );
  const [saving, setSaving] = useState(false);

  const updateAt = (i, patch) =>
    setPallets(cur => cur.map((p, k) => k === i ? { ...p, ...patch } : p));

  const addPalette = () => {
    if (pallets.length >= 5) return;
    setPallets(cur => [
      ...cur,
      { _key: Date.now(), hazardous: false, lengthM: '', widthM: '', heightM: '', weightKg: '' },
    ]);
  };
  const removeAt = (i) => setPallets(cur => cur.filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    const clean = pallets.map(p => ({
      hazardous: !!p.hazardous,
      lengthM:  parseFloat(String(p.lengthM || '').replace(',', '.'))  || 0,
      widthM:   parseFloat(String(p.widthM  || '').replace(',', '.'))  || 0,
      heightM:  parseFloat(String(p.heightM || '').replace(',', '.'))  || 0,
      weightKg: parseFloat(String(p.weightKg || '').replace(',', '.')) || 0,
    }));
    const r = await authFetch(`/api/integrations/easybill/orders/${orderId}/pallets`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pallets: clean }),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      // 01.09.2026 (Bug-Fix): Server-Response übernehmen, damit die IDs
      // stabil bleiben und die Inputs mit den bereinigten Zahlen refresht
      // werden (z. B. „1,2" → 1.2). Sonst bleiben die Rohtexte im State
      // liegen und ein zweiter Klick auf „Speichern" schickt sie erneut
      // durch parseFloat.
      if (Array.isArray(d.pallets) && d.pallets.length > 0) {
        setPallets(d.pallets.map((p, i) => ({ ...p, _key: p.id || i })));
      }
      toast.success('Paletten gespeichert');
      onSaved?.(d.pallets || []);
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  return (
    <div className="mt-2 ml-6 border-l-2 border-[#ff8000]/40 pl-3 space-y-2" data-testid="palette-editor">
      {pallets.map((p, i) => (
        <div key={p._key} className="bg-white border border-gray-200 rounded-lg p-2 space-y-2"
             data-testid={`palette-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-semibold text-[#7ca735] uppercase tracking-wide">
              {i + 1}. Palette
            </div>
            <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer">
              <input type="checkbox" checked={!!p.hazardous}
                     onChange={e => updateAt(i, { hazardous: e.target.checked })}
                     data-testid={`palette-${i}-hazardous`} />
              <span className={p.hazardous ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                Gefahrgut
              </span>
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { key: 'lengthM',  ph: 'L in m', suffix: 'm' },
              { key: 'widthM',   ph: 'B in m', suffix: 'm' },
              { key: 'heightM',  ph: 'H in m', suffix: 'm' },
              { key: 'weightKg', ph: 'Gewicht', suffix: 'kg' },
            ].map(f => (
              <div key={f.key} className="relative">
                <input type="text" inputMode="decimal"
                  value={p[f.key] ?? ''}
                  onChange={e => updateAt(i, { [f.key]: e.target.value })}
                  placeholder={f.ph}
                  className="w-full pr-8 pl-2 py-1 text-xs border border-gray-200 rounded"
                  data-testid={`palette-${i}-${f.key}`} />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">
                  {f.suffix}
                </span>
              </div>
            ))}
          </div>
          {pallets.length > 1 && (
            <button onClick={() => removeAt(i)}
                    className="text-[10px] text-red-500 hover:text-red-700"
                    data-testid={`palette-${i}-remove`}>
              × Palette entfernen
            </button>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2">
        {pallets.length < 5 && (
          <button onClick={addPalette}
                  className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
                  data-testid="palette-add">
            + Palette hinzufügen
          </button>
        )}
        <button onClick={save} disabled={saving}
                className="text-[11px] px-3 py-1 rounded bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50"
                data-testid="palette-save">
          {saving ? 'Speichere …' : 'Paletten speichern'}
        </button>
      </div>
    </div>
  );
}

